import { useRef } from "react";

/**
 * Returns a `keyOf(index)` function and an `onListChange` helper that keeps a
 * hidden per-item UID array in sync with the parent-owned `items` array.
 * Fixes React "key={index}" bugs in editable lists (focus loss on remove).
 *
 * Usage:
 *   const { keyOf, add, remove, update } = useListUids(items, onChange);
 *   items.map((it, i) => <input key={keyOf(i)} .../>);
 */
export function useListUids(items, onChange) {
  const uidsRef = useRef([]);
  // Grow the uid array if the parent list grew outside our helpers
  while (uidsRef.current.length < items.length) {
    uidsRef.current.push(`li-${Math.random().toString(36).slice(2, 10)}`);
  }
  while (uidsRef.current.length > items.length) {
    uidsRef.current.pop();
  }

  const keyOf = (index) => uidsRef.current[index];

  const add = (empty = "") => {
    uidsRef.current = [...uidsRef.current, `li-${Math.random().toString(36).slice(2, 10)}`];
    onChange([...items, empty]);
  };

  const remove = (index) => {
    uidsRef.current = uidsRef.current.filter((_, i) => i !== index);
    onChange(items.filter((_, i) => i !== index));
  };

  const update = (index, value) => {
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  return { keyOf, add, remove, update };
}
